import { resolveCompetitionFilters, resolveSeasonFilters } from '@statsbomb/kitbag-global-football/state';
import { atom, Getter } from 'jotai';
import { unwrap } from 'jotai/utils';
import { comparisonFiltersAtom } from '@/atoms/filters/comparison';
import { comparisonTeamCyclesAtom, comparisonTeamDefaultCycleAtom } from '@/atoms/queries/team/teamComparisonCycles';
import { convertOptionsToValues } from '@/utils/array';
import { createCompetitionOptionsAtom, createSeasonOptionsAtom } from '@/utils/atoms/cycles';
import { filterCyclesWithRelsByCompetition } from '@/utils/filters';

export const comparisonTeamCompetitionOptionsAtom = createCompetitionOptionsAtom(comparisonTeamCyclesAtom);

const unwrappedComparisonTeamCompetitionOptionsAtom = unwrap(comparisonTeamCompetitionOptionsAtom, prev => prev || []);

const readCompetitionOptions = (get: Getter) => {
  try {
    return get(unwrappedComparisonTeamCompetitionOptionsAtom);
  } catch {
    return [];
  }
};

const comparisonTeamDefaultCompetitionIdAtom = atom(async get => {
  const defaultCycle = await get(comparisonTeamDefaultCycleAtom);
  return defaultCycle?.competition.competitionId ?? null;
});

const unwrappedComparisonTeamDefaultCompetitionIdAtom = unwrap(
  comparisonTeamDefaultCompetitionIdAtom,
  prev => prev || null,
);

const readDefaultCompetitionId = (get: Getter) => {
  try {
    return get(unwrappedComparisonTeamDefaultCompetitionIdAtom);
  } catch {
    return null;
  }
};

export const comparisonTeamSelectedCompetitionsAtom = atom(get => {
  const competitionOptions = readCompetitionOptions(get);
  return resolveCompetitionFilters({
    selectedCompetitionIds: get(comparisonFiltersAtom).competitionIds,
    competitionOptions,
    defaultCompetitionId: readDefaultCompetitionId(get),
  }).selectedCompetitions;
});

export const comparisonTeamCompetitionIdsToFilterByAtom = atom(get => {
  const competitionOptions = readCompetitionOptions(get);
  return resolveCompetitionFilters({
    selectedCompetitionIds: get(comparisonFiltersAtom).competitionIds,
    competitionOptions,
    defaultCompetitionId: readDefaultCompetitionId(get),
  }).competitionIdsToFilterBy;
});

const filteredComparisonTeamCyclesSeasonsAtom = atom(async get => {
  const cycles = await get(comparisonTeamCyclesAtom);
  const selectedCompetitionIds = get(comparisonTeamCompetitionIdsToFilterByAtom);
  return filterCyclesWithRelsByCompetition(cycles, selectedCompetitionIds);
});

export const comparisonTeamSeasonOptionsAtom = createSeasonOptionsAtom(filteredComparisonTeamCyclesSeasonsAtom);

const unwrappedComparisonTeamSeasonOptionsAtom = unwrap(comparisonTeamSeasonOptionsAtom, prev => prev || null);

const readSeasonOptions = (get: Getter) => {
  try {
    return get(unwrappedComparisonTeamSeasonOptionsAtom);
  } catch {
    return null;
  }
};

export const comparisonTeamSelectedSeasonsAtom = atom(get => {
  const seasonOptions = readSeasonOptions(get);
  return resolveSeasonFilters({
    selectedSeasonIds: get(comparisonFiltersAtom).seasonIds,
    seasonOptions,
    currentYear: new Date().getFullYear(),
  }).selectedSeasons;
});

export const comparisonTeamSeasonIdsToFilterByAtom = atom(get => {
  const selectedSeasonIds = get(comparisonFiltersAtom).seasonIds;
  if (selectedSeasonIds) return selectedSeasonIds;
  return convertOptionsToValues(get(comparisonTeamSelectedSeasonsAtom));
});
